import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Checkbox,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";

interface PageOption {
  id: string;
  label: string;
}

interface PageSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedPages: string[]) => void;
  availablePages: PageOption[];
  initialSelection?: string[];
  isGenerating?: boolean;
  renderThumbnail: (pageId: string) => React.ReactNode;
  actionType?: "download" | "email" | null;
}

const PageSelectionModal: React.FC<PageSelectionModalProps> = ({
  open,
  onClose,
  onConfirm,
  availablePages,
  initialSelection,
  isGenerating = false,
  renderThumbnail,
  actionType = "download",
}) => {
  const [selectedPages, setSelectedPages] = useState<string[]>(
    initialSelection || availablePages.map((p) => p.id)
  );

  useEffect(() => {
    if (open && initialSelection) {
      setSelectedPages(initialSelection);
    }
  }, [open, initialSelection]);

  const handleTogglePage = (pageId: string) => {
    setSelectedPages((prev) =>
      prev.includes(pageId)
        ? prev.filter((id) => id !== pageId)
        : [...prev, pageId]
    );
  };

  const handleSelectAll = () => {
    setSelectedPages(availablePages.map((p) => p.id));
  };

  const handleDeselectAll = () => {
    setSelectedPages([]);
  };

  const handleConfirm = () => {
    if (selectedPages.length === 0) {
      return;
    }
    onConfirm(selectedPages);
  };

  const allSelected = selectedPages.length === availablePages.length;
  const someSelected = selectedPages.length > 0 && !allSelected;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Select Pages to {actionType === "email" ? "Email" : "Download"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Choose which pages to include in your{" "}
              {actionType === "email" ? " email" : " PDF"}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={allSelected ? handleDeselectAll : handleSelectAll}
            startIcon={
              allSelected ? (
                <CheckBoxIcon />
              ) : someSelected ? (
                <IndeterminateCheckBoxIcon />
              ) : (
                <CheckBoxOutlineBlankIcon />
              )
            }
            sx={{ textTransform: "none" }}
          >
            {allSelected ? "Deselect All" : "Select All"}
          </Button>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gap: 2,
          }}
        >
          {availablePages.map((page) => {
            const isSelected = selectedPages.includes(page.id);
            return (
              <Box
                key={page.id}
                onClick={() => handleTogglePage(page.id)}
                sx={{
                  cursor: "pointer",
                  position: "relative",
                  border: isSelected
                    ? "3px solid #614338"
                    : "3px solid #e0e0e0",
                  borderRadius: 2,
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  backgroundColor: isSelected ? "#f9f5f3" : "#fff",
                  "&:hover": {
                    border: "3px solid #614338",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {/* Checkbox Overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 2,
                    //backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "4px",
                    //padding: "2px",
                    // boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    sx={{
                      padding: 0,
                      color: "#614338",
                      "&.Mui-checked": {
                        color: "#614338",
                      },
                    }}
                  />
                </Box>

                {/* Page Number Badge */}
                {/* <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    zIndex: 2,
                    //backgroundColor: "#614338",
                    color: "#614338",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 600,
                    //boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  {index + 1}
                </Box> */}

                {/* Thumbnail */}
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "8.5/11",
                    bgcolor: "#fff",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      width: "816px",
                      minHeight: "1056px",
                      transform: "scale(0.24)",
                      transformOrigin: "top left",
                      pointerEvents: "none",
                    }}
                  >
                    {renderThumbnail(page.id)}
                  </Box>
                </Box>

                {/* Page Label */}
                <Box
                  sx={{
                    p: 1.5,
                    backgroundColor: isSelected ? "#614338" : "#f5f5f5",
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textAlign: "center",
                      color: isSelected ? "#fff" : "#333",
                      fontSize: "13px",
                    }}
                  >
                    {page.label}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {selectedPages.length === 0 && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: "#fff3cd",
              borderRadius: 1,
              border: "1px solid #ffc107",
            }}
          >
            <Typography variant="body2" color="#856404">
              Please select at least one page to download
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{selectedPages.length}</strong> of{" "}
            <strong>{availablePages.length}</strong> pages selected
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderColor: "#555" }}
          disabled={isGenerating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={selectedPages.length === 0 || isGenerating}
          sx={{
            bgcolor: "#614338",
            "&:hover": {
              bgcolor: "#4a332b",
            },
            "&.Mui-disabled": {
              bgcolor: "#ccc",
            },
          }}
        >
          {isGenerating
            ? actionType === "email"
              ? "Sending email..."
              : "Generating PDF..."
            : actionType === "email"
            ? "Send Email"
            : "Download PDF"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageSelectionModal;
